import { computed, Injectable, signal, WritableSignal } from "@angular/core";
import {
  ColumnKey,
  EarnMask,
  EarnMaskLookup,
  MASKABLE_COLUMN_IDS,
  EarnCellMap,
  EarnColumnMap,
  TransactionId,
} from "../../models/types/earn.types";
import { EarnTableView } from "../../models/types/transaction.types";

interface ILookupEarn {
  lookupMask: WritableSignal<EarnMaskLookup | null>;
}

@Injectable({
  providedIn: "root",
})
export class EarnLookup implements ILookupEarn {
  lookupMask: WritableSignal<EarnMaskLookup | null> = signal<EarnMaskLookup | null>(null);

  isAllRowsMasked = computed(() => {
    const selectedRowsSize = this.lookupMask()?.rows.size;
    const appliedToArraySize = this.lookupMask()?.appliedToRows.size;
    return selectedRowsSize === appliedToArraySize;
  });

  maskToLookup(baseMask: EarnMask) {
    this.lookupMask.set({
      rows: new Map(Object.entries(baseMask.rows || {})),
      columns: new Map(Object.entries(baseMask.columns || {})) as EarnColumnMap,
      cells: new Map(
        Object.entries(baseMask.cells || {}).map(([transactionId, columns]) => [
          transactionId,
          new Map(Object.entries(columns)) as EarnColumnMap,
        ]),
      ) as EarnCellMap,
      appliedToRows: new Set(baseMask.appliedToArray || []),
    });
  }

  applySearchFilterToLookup(searchResultIds: TransactionId[]) {
    const lookup = this.copyEarnMaskLookup(this.lookupMask());

    const newAppliedSet = new Set(searchResultIds);

    const isFilteringOut = newAppliedSet.size < lookup.appliedToRows.size;
    if (isFilteringOut) {
      lookup.rows = new Map([...lookup.rows].filter(([key]) => newAppliedSet.has(key)));
      lookup.cells = new Map([...lookup.cells].filter(([key]) => newAppliedSet.has(key)));
      lookup.appliedToRows = newAppliedSet;

      MASKABLE_COLUMN_IDS.forEach((colId) => {
        if (this.allRowsHaveColumnHidden(lookup, null, colId)) {
          this.checkColumn(lookup, colId);
        }
      });
    }

    const isFilteringIn = newAppliedSet.size > lookup.appliedToRows.size;
    if (isFilteringIn) {
      // for each lookup.columns,
      const aNewRowId = searchResultIds.find((id) => !this.lookupMask()?.appliedToRows.has(id)); // this is to be able to surpass the rowId condition in uncheckColumn, fix it later
      lookup.columns.forEach((_, colId) => {
        this.uncheckColumn(lookup, colId, aNewRowId);
      });
      lookup.appliedToRows = newAppliedSet;
    }

    this.lookupMask.set(lookup);
  }

  private copyEarnMaskLookup(src: EarnMaskLookup): EarnMaskLookup {
    return {
      rows: new Map(src.rows),
      columns: new Map(src.columns),
      cells: new Map(Array.from(src.cells, ([txId, columnMap]) => [txId, new Map(columnMap)])),
      appliedToRows: new Set(src.appliedToRows),
    };
  }

  private allRowsHaveColumnHidden(
    lookup: EarnMaskLookup,
    rowId: TransactionId,
    colId: ColumnKey,
  ): boolean {
    return Array.from(lookup.appliedToRows).every((appliedRowId) => {
      if (lookup.rows.has(appliedRowId)) {
        return true;
      }

      const appliedRowCellMap = lookup.cells.get(appliedRowId);
      return appliedRowCellMap && appliedRowCellMap?.has(colId);
    });
  }

  private checkColumn(lookup: EarnMaskLookup, colId: ColumnKey): EarnMaskLookup {
    lookup.columns.set(colId, true);
    // remove this column from all cell maps
    lookup.cells.forEach((cellColumnMap) => {
      if (cellColumnMap.has(colId)) {
        cellColumnMap.delete(colId);
      }
    });

    return lookup;
  }

  private uncheckColumn(
    lookup: EarnMaskLookup,
    colId: ColumnKey,
    rowId?: TransactionId,
  ): EarnMaskLookup {
    // uncheck the column
    lookup.columns.delete(colId);
    if (!rowId) {
      return lookup;
    }

    //now for the rest of the rows show the cell;
    lookup.appliedToRows.forEach((appliedRowId) => {
      if (appliedRowId !== rowId) {
        let cellColumnMap = lookup.cells.get(appliedRowId);
        if (!cellColumnMap) {
          cellColumnMap = new Map<ColumnKey, true>();
        }
        cellColumnMap.set(colId, true);
        lookup.cells.set(appliedRowId, cellColumnMap);
      }
    });
    return lookup;
  }

  toggleColumn(colId: ColumnKey) {
    let lookup = this.copyEarnMaskLookup(this.lookupMask());

    const columnMap = lookup.columns.get(colId);
    if (columnMap) {
      lookup = this.uncheckColumn(lookup, colId);
    }

    if (!columnMap) {
      lookup = this.checkColumn(lookup, colId);
    }

    this.lookupMask.set(lookup);
  }

  toggleRow(rowId: TransactionId) {
    let lookup = this.copyEarnMaskLookup(this.lookupMask());

    const rowMap = lookup.rows.get(rowId);
    if (rowMap) {
      lookup = this.uncheckRow(lookup, rowId);
    }

    if (!rowMap) {
      lookup = this.checkRow(lookup, rowId);
    }

    this.lookupMask.set(lookup);
  }

  toggleAllRows() {
    let lookup = this.copyEarnMaskLookup(this.lookupMask());

    const allRowsMasked = this.isAllRowsMasked();
    if (allRowsMasked) {
      // uncheck all rows
      lookup.appliedToRows.forEach((rowId) => {
        lookup = this.uncheckRow(lookup, rowId);
      });
    } else {
      // check all rows
      lookup.appliedToRows.forEach((rowId) => {
        lookup = this.checkRow(lookup, rowId);
      });
    }

    this.lookupMask.set(lookup);
  }

  private checkRow(lookup: EarnMaskLookup, rowId: TransactionId): EarnMaskLookup {
    lookup.rows.set(rowId, true);
    // remove all its cells from lookup
    lookup.cells.delete(rowId);

    return lookup;
  }

  private uncheckRow(
    lookup: EarnMaskLookup,
    rowId: TransactionId,
    colId?: keyof EarnTableView,
  ): EarnMaskLookup {
    // uncheck  the row
    lookup.rows.delete(rowId);
    if (!colId) {
      return lookup;
    }

    // Now, for all columns in that row except for the colId one, set them as hidden (false)
    const columnMap: EarnColumnMap = new Map();
    MASKABLE_COLUMN_IDS.forEach((columnKey) => {
      if (columnKey !== colId) {
        columnMap.set(columnKey, true);
      }
    });
    lookup.cells.set(rowId, columnMap);

    return lookup;
  }

  private uncheckCell(
    lookup: EarnMaskLookup,
    rowId: TransactionId,
    colId: ColumnKey,
  ): EarnMaskLookup {
    const cellMap = lookup.cells.get(rowId);
    if (!cellMap) {
      return lookup;
    }

    const cellColMap = cellMap.get(colId);
    if (cellColMap) {
      cellMap.delete(colId);
    }

    return lookup;
  }

  checkCell(lookup: EarnMaskLookup, rowId: TransactionId, colId: ColumnKey): EarnMaskLookup {
    const hasCellRow = lookup.cells.get(rowId);
    if (hasCellRow) {
      hasCellRow.set(colId, true);
    } else {
      const newCellColMap: EarnColumnMap = new Map();
      newCellColMap.set(colId, true);
      lookup.cells.set(rowId, newCellColMap);
    }

    // now I need to check if all rows have this column visible, then I can remove the column from cell map and add it to column map
    if (this.allRowsHaveColumnHidden(lookup, rowId, colId)) {
      lookup = this.checkColumn(lookup, colId);
    }

    if (this.allColumnsHiddenForRow(lookup, rowId, colId)) {
      lookup = this.checkRow(lookup, rowId);
    }

    return lookup;
  }

  allColumnsHiddenForRow(lookup: EarnMaskLookup, rowId: TransactionId, colId: ColumnKey): boolean {
    const cellColMap = lookup.cells.get(rowId);
    if (!cellColMap) {
      return false;
    }

    return MASKABLE_COLUMN_IDS.every(
      (columnKey) => cellColMap.has(columnKey) || lookup.columns.has(columnKey),
    );
  }

  toggleCell(rowId: TransactionId, colId: ColumnKey) {
    let lookup = this.copyEarnMaskLookup(this.lookupMask());

    const isMaskedOnRow = this.isCellMaskedOnRow(rowId, lookup);
    //if Row is hidden and cell is hidden because of the row
    if (isMaskedOnRow) {
      lookup = this.uncheckRow(lookup, rowId, colId);
    }

    const isMaskedOnColumn = this.isCellMaskedOnColumn(colId, lookup);
    // if Column is hidden and cell is hidden because of the column
    if (isMaskedOnColumn) {
      lookup = this.uncheckColumn(lookup, colId, rowId);
    }

    if (isMaskedOnRow || isMaskedOnColumn) {
      this.lookupMask.set(lookup);
      return;
    }

    const isMaskedOnSelf = this.isCellMaskedOnSelf(rowId, colId, lookup);

    //If not checked, then chekc it
    if (!isMaskedOnSelf) {
      lookup = this.checkCell(lookup, rowId, colId);
      this.lookupMask.set(lookup);
      return;
    }

    //here it was checked, so uncheck it
    lookup = this.uncheckCell(lookup, rowId, colId);

    this.lookupMask.set(lookup);
    this.isCellMasked(rowId, colId);
  }

  isCellMaskedOnRow(rowId: TransactionId, lookup: EarnMaskLookup): boolean {
    return lookup.rows.get(rowId);
  }
  isCellMaskedOnColumn(colId: ColumnKey, lookup: EarnMaskLookup): boolean {
    return lookup.columns.get(colId);
  }
  isCellMaskedOnSelf(rowId: TransactionId, colId: ColumnKey, lookup: EarnMaskLookup): boolean {
    const rowForCell = lookup.cells.get(rowId);
    if (!rowForCell) {
      return false;
    }
    return rowForCell.get(colId);
  }

  isCellMasked(rowId: TransactionId, colId: ColumnKey): boolean {
    const lookup = this.lookupMask();
    if (!lookup) {
      return false;
    }

    // Column level check
    if (lookup.columns.get(colId)) {
      return true;
    }

    // Row level check
    if (lookup.rows.get(rowId)) {
      return true;
    }

    // Cell level check
    if (lookup.cells.get(rowId)?.has(colId)) {
      return true;
    }

    return false;
  }
}
